/**
 * DateService
 *
 * Service for handling flexible date operations (Phase 2).
 * Supports year-only dates, GEDCOM date qualifiers, and date formatting.
 *
 * @module services/DateService
 */

import type { DateQualifier } from '@/types/entities';

/**
 * DateService
 *
 * Provides utility methods for working with flexible dates.
 * Handles year-only dates, date qualifiers, and validation.
 */
export class DateService {
  /**
   * Get year from date or year field
   * Prefers full date if available, falls back to year
   *
   * @param date - Full date string (ISO format)
   * @param year - Year-only integer
   * @returns Year as number, or null if neither provided
   */
  public getYear(date: string | null, year: number | null): number | null {
    if (date) {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.getFullYear();
      }
    }
    return year || null;
  }

  /**
   * Format date with optional qualifier prefix
   * Handles full dates, year-only dates, and qualifiers
   *
   * @param date - Full date string (ISO format)
   * @param year - Year-only integer
   * @param qualifier - GEDCOM date qualifier
   * @param locale - Locale for date formatting (default: 'en-US')
   * @returns Formatted date string
   *
   * @example
   * formatDate('1850-03-15', null, null) => '3/15/1850'
   * formatDate(null, 1850, null) => '1850'
   * formatDate(null, 1850, 'ABT') => 'circa 1850'
   * formatDate('1850-03-15', 1850, 'BEF') => 'BEF 3/15/1850'
   */
  public formatDate(
    date: string | null,
    year: number | null,
    qualifier: DateQualifier | null,
    locale: string = 'en-US'
  ): string {
    const prefix = this.getQualifierPrefix(qualifier);

    if (date) {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        const formattedDate = parsedDate.toLocaleDateString(locale);
        return `${prefix}${formattedDate}`;
      }
    }

    if (year) {
      return `${prefix}${year}`;
    }

    return 'Unknown';
  }

  /**
   * Get qualifier prefix for display
   * Converts GEDCOM qualifiers to human-readable text
   *
   * @param qualifier - GEDCOM date qualifier
   * @returns Prefix string with trailing space
   * @private
   */
  private getQualifierPrefix(qualifier: DateQualifier | null): string {
    if (!qualifier) return '';
    if (qualifier.length === 0) return '';

    switch (qualifier) {
      case 'ABT':
        return 'circa ';
      case 'BEF':
        return 'before ';
      case 'AFT':
        return 'after ';
      case 'CAL':
        return 'calculated ';
      case 'EST':
        return 'estimated ';
      default:
        return `${qualifier} `;
    }
  }

  /**
   * Validate date/year consistency
   * Ensures that if both date and year are provided, the year matches
   *
   * @param date - Full date string
   * @param year - Year-only integer
   * @returns Validation result with error message if invalid
   */
  public validateDateYearConsistency(
    date: string | null,
    year: number | null
  ): { valid: boolean; error?: string } {
    if (date && year) {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return {
          valid: false,
          error: 'Invalid date format',
        };
      }

      const dateYear = parsedDate.getFullYear();
      if (dateYear !== year) {
        return {
          valid: false,
          error: `Date year (${dateYear}) does not match year field (${year})`,
        };
      }
    }
    return { valid: true };
  }

  /**
   * Extract year from full date string
   * Helper method for getting year from date
   *
   * @param date - Full date string (ISO format)
   * @returns Year as number, or null if invalid
   */
  public extractYear(date: string | null): number | null {
    if (!date) return null;

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return null;

    return parsedDate.getFullYear();
  }

  /**
   * Compare two flexible dates
   * Returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
   * Handles year-only dates gracefully
   *
   * @param date1 - First date info
   * @param date2 - Second date info
   * @returns Comparison result (-1, 0, or 1)
   */
  public compareDates(
    date1: { date: string | null; year: number | null },
    date2: { date: string | null; year: number | null }
  ): number {
    const year1 = this.getYear(date1.date, date1.year);
    const year2 = this.getYear(date2.date, date2.year);

    // If both have years, compare them
    if (year1 !== null && year2 !== null) {
      if (year1 < year2) return -1;
      if (year1 > year2) return 1;

      // Same year - if both have full dates, compare them
      if (date1.date && date2.date) {
        const time1 = new Date(date1.date).getTime();
        const time2 = new Date(date2.date).getTime();
        if (time1 < time2) return -1;
        if (time1 > time2) return 1;
      }

      return 0;
    }

    // If only one has a year, it comes first
    if (year1 !== null) return -1;
    if (year2 !== null) return 1;

    // Neither has a year
    return 0;
  }

  /**
   * Format flexible date with GEDCOM qualifier
   * For use in person node visualization
   *
   * @param year - Year-only integer
   * @param qualifier - GEDCOM date qualifier
   * @returns Formatted string with qualifier prefix
   *
   * @example
   * formatFlexibleDate(1850, 'ABT') => "~1850"
   * formatFlexibleDate(1850, 'BEF') => "bef. 1850"
   * formatFlexibleDate(1850, 'AFT') => "aft. 1850"
   * formatFlexibleDate(1850, 'CAL') => "cal. 1850"
   * formatFlexibleDate(1850, 'EST') => "est. 1850"
   * formatFlexibleDate(1850, '') => "1850"
   * formatFlexibleDate(null, 'ABT') => ""
   */
  public formatFlexibleDate(
    year: number | null,
    qualifier: DateQualifier | null | undefined
  ): string {
    if (!year) return '';

    const yearStr = year.toString();

    // Handle empty string or null/undefined qualifier as exact
    if (!qualifier) return yearStr;
    if (qualifier.length === 0) return yearStr;

    switch (qualifier) {
      case 'ABT':
        return `~${yearStr}`;
      case 'BEF':
        return `bef. ${yearStr}`;
      case 'AFT':
        return `aft. ${yearStr}`;
      case 'CAL':
        return `cal. ${yearStr}`;
      case 'EST':
        return `est. ${yearStr}`;
      default:
        return yearStr;
    }
  }

  /**
   * Get full qualifier label for tooltip/explanation
   * Converts GEDCOM qualifiers to human-readable explanations
   *
   * @param qualifier - GEDCOM date qualifier
   * @returns Full explanation text
   *
   * @example
   * getQualifierLabel('ABT') => "About"
   * getQualifierLabel('BEF') => "Before"
   * getQualifierLabel('AFT') => "After"
   * getQualifierLabel('CAL') => "Calculated"
   * getQualifierLabel('EST') => "Estimated"
   * getQualifierLabel('') => "Exact"
   * getQualifierLabel(null) => "Exact"
   */
  public getQualifierLabel(qualifier: DateQualifier | null | undefined): string {
    if (!qualifier) return 'Exact';
    if (qualifier.length === 0) return 'Exact';

    switch (qualifier) {
      case 'ABT':
        return 'About';
      case 'BEF':
        return 'Before';
      case 'AFT':
        return 'After';
      case 'CAL':
        return 'Calculated';
      case 'EST':
        return 'Estimated';
      default:
        return 'Exact';
    }
  }

  /**
   * Format marriage date range for couple box display (Phase 3 Slice 4)
   * Formats date_start and date_end into a human-readable range
   *
   * @param dateStart - Marriage start date (ISO string or null)
   * @param dateEnd - Marriage end date (ISO string or null)
   * @param hasEnded - Whether the marriage/partnership has ended
   * @returns Formatted date range string
   *
   * @example
   * formatMarriageDateRange('1980-01-01', null, false) => "1980 - present"
   * formatMarriageDateRange('1980-01-01', '1990-12-31', true) => "1980 - 1990"
   * formatMarriageDateRange('1980-01-01', null, true) => "1980 - ?"
   * formatMarriageDateRange(null, null, false) => ""
   */
  public formatMarriageDateRange(
    dateStart: string | null | undefined,
    dateEnd: string | null | undefined,
    hasEnded: boolean | null | undefined
  ): string {
    // No start date, no display
    if (!dateStart) return '';

    // Extract year from start date
    const startYear = this.extractYear(dateStart);
    if (!startYear) return '';

    // Has end date - show range
    if (dateEnd) {
      const endYear = this.extractYear(dateEnd);
      return endYear ? `${startYear} - ${endYear}` : `${startYear} - ?`;
    }

    // No end date but has ended - show with question mark
    if (hasEnded) {
      return `${startYear} - ?`;
    }

    // Ongoing relationship - show with "present"
    return `${startYear} - present`;
  }
}

// ==================== SINGLETON INSTANCE ====================

/**
 * Singleton instance of DateService
 * Use this throughout the application for consistency
 */
export const dateService = new DateService();
